/**
 * Schedules API Service
 */

import api from './api';
import type { ApiResponse, DriverSchedule, WeeklyTimeSlot } from '../types';

export interface CreateScheduleParams {
  driverUid: string;
  weeklyTemplate?: WeeklyTimeSlot[];
  vehicleCapacity?: number;
  defaultStartLocation?: { lng: number; lat: number };
}

export interface UpdateScheduleParams {
  weeklyTemplate?: WeeklyTimeSlot[];
  vehicleCapacity?: number;
  zoneIds?: string[];
  defaultStartLocation?: { lng: number; lat: number };
  isActive?: boolean;
  /**
   * Vehicle capabilities, e.g. `['cold_chain']`. Omit to leave them alone —
   * every other edit on this page sends no capabilities field, and the backend
   * treats absent as "unchanged" so a polygon or capacity edit cannot silently
   * un-flag a refrigerated van. Send `[]` to clear.
   */
  capabilities?: string[];
  /**
   * Ops label for where the vehicle is based (PRD §8). Free text, never a zone.
   * Omit to leave alone; `null` or `''` clears it.
   */
  homeZoneLabel?: string | null;
}

export interface AddExceptionParams {
  date: string;
  start?: string;
  end?: string;
  reason?: string;
}

/**
 * Get all schedules
 */
export async function getAllSchedules(): Promise<DriverSchedule[]> {
  const response = await api.get<ApiResponse<DriverSchedule[]>>('/schedules');
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch schedules');
  }
  
  return response.data.data;
}

/**
 * Get schedule for a driver
 */
export async function getSchedule(driverUid: string): Promise<DriverSchedule> {
  const response = await api.get<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch schedule');
  }
  
  return response.data.data;
}

/**
 * Create a new schedule
 */
export async function createSchedule(params: CreateScheduleParams): Promise<DriverSchedule> {
  const response = await api.post<ApiResponse<DriverSchedule>>('/schedules', params);
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to create schedule');
  }
  
  return response.data.data;
}

/**
 * Update a schedule
 */
export async function updateSchedule(
  driverUid: string,
  params: UpdateScheduleParams
): Promise<DriverSchedule> {
  const response = await api.put<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}`,
    params
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to update schedule');
  }
  
  return response.data.data;
}

/**
 * Delete a schedule
 */
export async function deleteSchedule(driverUid: string): Promise<void> {
  const response = await api.delete<ApiResponse<void>>(`/schedules/${driverUid}`);
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to delete schedule');
  }
}

/**
 * Set weekly template
 */
export async function setWeeklyTemplate(
  driverUid: string,
  template: WeeklyTimeSlot[]
): Promise<DriverSchedule> {
  const response = await api.put<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}/template`,
    { template }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to set weekly template');
  }
  
  return response.data.data;
}

/**
 * Copy weekly template from another driver
 */
export async function copyWeeklyTemplate(
  toDriverUid: string,
  fromDriverUid: string
): Promise<DriverSchedule> {
  const response = await api.post<ApiResponse<DriverSchedule>>(
    `/schedules/${toDriverUid}/template/copy`,
    { fromDriverUid }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to copy weekly template');
  }
  
  return response.data.data;
}

/**
 * Add an exception
 */
export async function addException(
  driverUid: string,
  exception: AddExceptionParams
): Promise<DriverSchedule> {
  const response = await api.post<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}/exceptions`,
    exception
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to add exception');
  }
  
  return response.data.data;
}

/**
 * Remove an exception
 */
export async function removeException(
  driverUid: string,
  date: string
): Promise<DriverSchedule> {
  const response = await api.delete<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}/exceptions/${date}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to remove exception');
  }
  
  return response.data.data;
}

/**
 * Add multiple exceptions
 */
export async function addBulkExceptions(
  driverUid: string,
  exceptions: AddExceptionParams[]
): Promise<DriverSchedule> {
  const response = await api.post<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}/exceptions/bulk`,
    { exceptions }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to add bulk exceptions');
  }
  
  return response.data.data;
}

/**
 * Mark driver as unavailable for a date range
 */
export async function markUnavailable(
  driverUid: string,
  startDate: string,
  endDate: string,
  reason?: string
): Promise<DriverSchedule> {
  const response = await api.post<ApiResponse<DriverSchedule>>(
    `/schedules/${driverUid}/unavailable`,
    { startDate, endDate, reason }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to mark unavailable');
  }
  
  return response.data.data;
}

/**
 * Get default weekly template
 */
export async function getDefaultTemplate(): Promise<WeeklyTimeSlot[]> {
  const response = await api.get<ApiResponse<WeeklyTimeSlot[]>>(
    '/schedules/defaults/template'
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch default template');
  }
  
  return response.data.data;
}
